/**
 * Reassign Case modal for the Pharma STP File table.
 * Presents a filename scope plus From / To POC selects; any combination may be submitted.
 */

import { useEffect, useMemo, useState } from "react";
import { Modal, Select } from "risa-oasis-ui_v2";
import { POC_OPTIONS } from "../../../constants/submissionSummaryOptions";
import { ModalId } from "../../../enums/modalId";
import { LoaderMessage } from "../../loaderMessage/loaderMessage";
import { useFilenameSummaryOptions } from "./useFilenameSummaryOptions";

/** Optional POC value for one side of a reassignment. */
export interface ReassignCaseFieldSelections {
  poc?: string;
}

export interface ReassignCasePayload {
  /** Optional batch filename that scopes which rows are reassigned. */
  filename?: string;
  from: ReassignCaseFieldSelections;
  to: ReassignCaseFieldSelections;
}

export interface ReassignCaseModalProps {
  /** Called when the user dismisses the dialog without assigning. */
  onClose: () => void;
  /**
   * Called when the user confirms assignment.
   * If omitted, the modal closes after Assign.
   */
  onAssign?: (payload: ReassignCasePayload) => void | Promise<void>;
}

const POC_SELECT_OPTIONS = POC_OPTIONS.map((opt) => ({
  value: opt.id,
  label: opt.text,
}));

interface ReassignCaseSideFieldsProps {
  label: string;
  namePrefix: string;
  pocValue: string;
  onPocChange: (value: string) => void;
}

/**
 * Renders an optional POC select for the From or To side of reassignment.
 *
 * @param props - Side label, field ids, value, and change handler
 * @returns Labeled POC select block
 */
const ReassignCaseSideFields = ({
  label,
  namePrefix,
  pocValue,
  onPocChange,
}: ReassignCaseSideFieldsProps) => (
  <div className="flex flex-col gap-3">
    <div className="text-sm font-semibold text-primaryGray-2">{label}</div>
    <Select
      id={`reassign-case-${namePrefix}-poc`}
      label="POC"
      placeholder="Select"
      options={POC_SELECT_OPTIONS}
      defaultValue={pocValue}
      onOptionChange={(data) => onPocChange(data.value ?? "")}
    />
  </div>
);

/**
 * Builds optional `poc` entries for API filters or update payloads.
 *
 * @param selections - Raw select values from the modal
 * @returns Only the fields the user selected
 */
export const buildReassignCaseFieldRecord = (
  selections: ReassignCaseFieldSelections,
): Record<string, string> => {
  const record: Record<string, string> = {};
  if (selections.poc) {
    record.poc = selections.poc;
  }
  return record;
};

/**
 * Modal shell for reassigning cases with a filename scope and independent From/To POC selects.
 *
 * @param props - Close and optional assign callbacks
 * @returns Modal element registered with Oasis via `dialogId`
 */
const ReassignCaseModal = ({ onClose, onAssign }: ReassignCaseModalProps) => {
  const {
    options: filenameOptions,
    loading: filenameOptionsLoading,
    error: filenameOptionsError,
  } = useFilenameSummaryOptions();
  const [filename, setFilename] = useState("");
  const [fromPoc, setFromPoc] = useState("");
  const [toPoc, setToPoc] = useState("");
  const [isReassigning, setIsReassigning] = useState(false);

  const filenameSelectOptions = useMemo(
    () =>
      filenameOptions.map((option) => ({
        value: option,
        label: option,
      })),
    [filenameOptions],
  );

  useEffect(() => {
    setFilename("");
    setFromPoc("");
    setToPoc("");
  }, []);

  const handleAssign = async () => {
    setIsReassigning(true);
    try {
      if (onAssign) {
        await onAssign({
          ...(filename ? { filename } : {}),
          from: {
            ...(fromPoc ? { poc: fromPoc } : {}),
          },
          to: {
            ...(toPoc ? { poc: toPoc } : {}),
          },
        });
      }
    } finally {
      setIsReassigning(false);
      onClose();
    }
  };

  return (
    <Modal
      dialogId={ModalId.REASSIGN_CASE_MODAL}
      title="Reassign Case"
      saveButtonText={isReassigning ? "Reassigning..." : "Assign"}
      cancelText="Cancel"
      onCancel={onClose}
      onClose={onClose}
      onSave={handleAssign}
      disableSave={
        isReassigning || filenameOptionsLoading || filenameOptionsError != null
      }
    >
      <div className="relative flex min-h-[280px] flex-col">
        {filenameOptionsLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoaderMessage message="Loading filenames…" />
          </div>
        )}
        {!filenameOptionsLoading && filenameOptionsError != null && (
          <div className="flex min-h-[280px] items-center justify-center text-small text-red-600">
            Failed to load filenames. Please close and try again.
          </div>
        )}
        {!filenameOptionsLoading && filenameOptionsError == null && (
          <div className="flex flex-col gap-4">
            <Select
              id="reassign-case-filename"
              label="Filename"
              placeholder="Select filename"
              options={filenameSelectOptions}
              defaultValue={filename}
              onOptionChange={(data) => setFilename(data.value ?? "")}
            />
            <ReassignCaseSideFields
              label="From"
              namePrefix="from"
              pocValue={fromPoc}
              onPocChange={setFromPoc}
            />
            <ReassignCaseSideFields
              label="To"
              namePrefix="to"
              pocValue={toPoc}
              onPocChange={setToPoc}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReassignCaseModal;
